//
// Utility function to fetch user token from our server
// and cache it until expiration.
//

import "whatwg-fetch";
import { parseISO, addMinutes, isAfter } from "date-fns";
import { FetchError } from "./fetch-error";
import { Timer } from "./timer";
import { CustomDimensions } from "../telemetry";
import { Utils } from "./utils";
import { assign } from "lodash";
import * as uuid from "uuid";
import { ParamsParserUtils } from "./params-parser-utils";

export interface UserToken {
  jwtToken: string;
  expirationTime: string;
}

let token: Promise<UserToken> | undefined;

let authEndpoint = "/auth/token";
const tokenExpirationWindowInMinutes = 10;

export class HttpTokenUtils {
  public static fetchToken(): Promise<UserToken> {
    // Start the lap
    authEndpoint = ParamsParserUtils.parse(authEndpoint, {
      ...((Utils.isIe() && { nocache: Utils.randomCacheBusterValue() }) || undefined)
    });
    const start = Timer.startLap();
    const operationId = uuid.v4();
    const headers = new Headers();
    headers.append("diagnostic-context", operationId);

    const telemetryProps: CustomDimensions = {
      urlTemplate: authEndpoint,
      diagnosticContext: operationId
    };

    return fetch(authEndpoint, {
      method: "get",
      headers: headers,
      credentials: "include"
    }).then(
      (response: Response) => {
        // End the lap
        assign(telemetryProps, {
          id: operationId,
          url: response.url,
          path: authEndpoint,
          method: "GET",
          success: response.ok,
          status: response.status
        });
        Timer.endLapAndTrack(start, telemetryProps);

        if (response.ok) {
          return response.json() as Promise<UserToken>;
        } else {
          throw new FetchError(response);
        }
      },
      (error: Error) => {
        // End the lap
        assign(telemetryProps, {
          path: authEndpoint,
          method: "GET",
          success: false
        });
        Timer.endLapAndTrack(start, telemetryProps);

        throw new FetchError(error);
      }
    );
  }

  public static getToken(options: { forceRefreshToken: boolean | undefined }) {
    const { forceRefreshToken = false } = options;

    return this.getCurrentToken(forceRefreshToken)
      .then((token: UserToken) => {
        // date-fns 2.0 parseISO() is better for dates structured like `2019-09-28T19:40:46.242Z`
        const expiration = parseISO(token.expirationTime);
        const now = new Date();
        if (isAfter(addMinutes(now, tokenExpirationWindowInMinutes), expiration)) {
          // Expired or about to, get a new token
          return this.getCurrentToken(true);
        }
        // Otherwise we're still good, return what we've got
        return token;
      })
      .catch((err: FetchError) => {
        // Wipe out cache so we retry next time
        token = undefined;
        // and continue with the failure
        throw err;
      });
  }

  private static getCurrentToken(forceRefreshToken: boolean): Promise<UserToken> {
    if (!token || forceRefreshToken) {
      token = this.fetchToken();
    }
    return token;
  }
}
